import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { 
    Container, Grid, Paper, Typography, Box, Chip, Divider, Button, Avatar, Stack, 
    Dialog,
    DialogTitle,
    DialogContent,
    useMediaQuery
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { imagesURL } from "../../../apiRoutes/apiBase";
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Componentes Comunes
import QUDProgress from "../../../components/progress";
import { getSubscriptionDetail } from "../../../controllers/customer/subscriptions/ClientSubscriptionController";
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import SectionTitle from "../../../components/sectionTitle";
import PaymentContainer from "../../../components/payments/PaymentContainer";
import UserContext from "../../../navigation/context/userContext";

// Controlador


export default function SubscriptionDetail() {
    const { id } = useParams();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const { userData } = useContext(UserContext);
    const [subscription, setSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isPayModalOpen, setIsPayModalOpen] = useState(false);
    const [selectedOrderToPay,setSelectedOrderToPay] = useState(null);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        setIsLoading(true);
        const response = await getSubscriptionDetail(id);
        if (response.success) {
            setSubscription(response.data);
        } else {
            // Manejar error (ej. redirigir)
            history.push('/client/subscriptions');
        }
        setIsLoading(false);
    };

    const handlePayOrder = (orderToPay) => {
        // Aquí rediriges a tu flujo de pago existente
        // Ejemplo: history.push(`/checkout/order/${orderId}`);
        console.log("Ir a pagar orden:", orderToPay);
        setSelectedOrderToPay(orderToPay);
    };

    useEffect(() => {
        if (selectedOrderToPay) {
            setIsPayModalOpen(true);
        }
    }, [selectedOrderToPay]);
    
    const handleClosePayModal = () => {
        setIsPayModalOpen(false);
        // Es buena práctica esperar a que cierre la animación antes de limpiar el objeto
        setTimeout(() => setSelectedOrderToPay(null), 300); 
    };

    const getStatusColor = (status) => {
        const map = { active: 'success', past_due: 'error', pending: 'warning', canceled: 'default' };
        return map[status] || 'default';
    };

    const getStatusLabel = (status) => {
        const map = { active: 'Activa', past_due: 'Morosa', pending: 'Pendiente', canceled: 'Cancelada' };
        return map[status] || status;
    };

    if (isLoading) return <QUDProgress open={true} />;
    if (!subscription) return null;

    return (
        <Container maxWidth="xl" sx={{ mb: 8 }}>
            <CustomBreadcrumbs
                returnTo='/subscriptions'
                crumbs={[
                    {name:'Suscripciones',link:'/subscriptions'},
                    {name:'Detalle',link:`/subscriptions/detail/${id}`}
                ]}
            />

            <SectionTitle
                title='Detalle de suscripción'
                //subtitle={`Gestiona tus planes activos y revisa tus pagos pendientes.`}
            />

            {/* --- ENCABEZADO --- */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                    <Box>
                        <Typography variant="overline" color="textSecondary">
                            Plan Contratado
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                            {subscription.plan.name}
                        </Typography>
                    </Box>
                    <Chip 
                        label={getStatusLabel(subscription.status)} 
                        color={getStatusColor(subscription.status)} 
                        sx={{ fontSize: '1rem', height: 32, px: 1 }}
                    />
                </Box>
                <Box mt={2}>
                    <Typography variant="h5" fontWeight="bold">
                        ${parseFloat(subscription.plan.price).toFixed(2)} 
                        <Typography component="span" variant="body1" color="textSecondary" ml={1}>
                            / {subscription.plan.interval_count > 1 ? `Cada ${subscription.plan.interval_count} ` : ''}{subscription.plan.billing_interval}
                        </Typography>
                    </Typography>
                </Box>
            </Paper>

            <Grid container spacing={3} mb={4}>
                {/* --- INFO ACADEMIA --- */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>ACADEMIA / COMERCIO</Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                            <Avatar 
                                src={subscription.merchant.cover_image ? `${imagesURL}/${subscription.merchant.cover_image}` : ''} 
                                sx={{ width: 56, height: 56, mr: 2 }}
                            />
                            <Typography variant="h6">{subscription.merchant.name}</Typography>
                        </Box>
                    </Paper>
                </Grid>
                
                {/* --- INFO ALUMNO --- */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>BENEFICIARIO (ALUMNO)</Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                            <Avatar 
                                src={subscription.beneficiary.profile_image ? `${imagesURL}/${subscription.beneficiary.profile_image}` : ''} 
                                sx={{ width: 56, height: 56, mr: 2 }}
                            />
                            <Box>
                                <Typography variant="h6">
                                    {subscription.beneficiary.name} {subscription.beneficiary.last_name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    ID: {subscription.beneficiary.id}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* --- HISTORIAL DE PAGOS (ORDERS) --- */}
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
                Historial de Pagos
            </Typography>
            
            <Stack spacing={2}>
                {subscription.orders && subscription.orders.length > 0 ? (
                    subscription.orders.map((order) => (
                        <Paper key={order.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Grid container alignItems="center" spacing={2}>
                                {/* Fecha y Código */}
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="body2" color="textSecondary">
                                        {new Date(order.created_at).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {order.codigo}
                                    </Typography>
                                </Grid>

                                {/* Concepto */}
                                <Grid item xs={6} sm={4}>
                                    <Typography variant="body2">
                                        Renovación {subscription.plan.name}
                                    </Typography>
                                </Grid>

                                {/* Monto y Estado */}
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="h6" fontWeight="bold">
                                        ${parseFloat(order.monto).toFixed(2)}
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        color={(order.estatus === 2 || order.estatus === 6) ? 'success.main' : (order.estatus === 1 ? 'warning.main' : 'error.main')}
                                        fontWeight="bold"
                                    >
                                        {(order.estatus === 2 || order.estatus === 6) ? 'PAGADO' : (order.estatus === 1 ? 'PENDIENTE' : 'CANCELADO')}
                                    </Typography>
                                </Grid>

                                {/* Botón de Acción */}
                                <Grid item xs={6} sm={2} display="flex" justifyContent="flex-end">
                                    {/* CONDICIÓN: Si está pendiente Y soy el pagador */}
                                    {order.estatus === 1 && subscription.is_payer && (
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            size="small"
                                            onClick={() => handlePayOrder(order)}
                                        >
                                            Pagar
                                        </Button>
                                    )}

                                    {/* Mensaje alternativo para el Beneficiario (Opcional) */}
                                    {order.estatus === 1 && !subscription.is_payer && (
                                        <Chip label="Pendiente de Pago" size="small" variant="outlined" color="warning" />
                                    )}

                                    {/* Si está pagado */}
                                    {(order.estatus === 2 || order.estatus === 6) && (
                                        <Button variant="text" disabled sx={{ color: 'success.main' }}>PAGADO</Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    ))
                ) : (
                    <Typography color="textSecondary">No hay historial de pagos aún.</Typography>
                )}
            </Stack>


            <Dialog
                maxWidth={'sm'}
                fullWidth
                fullScreen={isMobile}
                open={isPayModalOpen}
                onClose={() => handleClosePayModal()}
            >
                <DialogTitle>Realizar Pago</DialogTitle>
                <DialogContent>
                    <PaymentContainer 
                        order={selectedOrderToPay} 
                        merchantUuid={subscription.merchant.uuid}
                        customer={{
                            nombre: userData.entity.name,
                            apellido: userData.entity.last_name,
                            documento_tipo: userData.entity.document_type,
                            documento: userData.entity.document_number,
                            correo:userData.user.email,
                            prefijo:userData.user.prefijo,
                            telefono: userData.entity.phone,
                            profesion: userData.entity.profession
                        }}
                        onPaymentSuccess={() => {
                            setIsPayModalOpen(false);
                            alert("¡Pago exitoso!");
                            loadData(); // Recargar historial para ver el cambio a 'Pagado'
                        }}
                    />
                </DialogContent>
            </Dialog>

        </Container>
    );
}